import Pusher from 'pusher-js';
import { useCallback, useEffect, useState } from 'react';
import { shallow } from 'zustand/shallow';

import {
  BlockOutputAiUsageRecordedData,
  BlockOutputEvaluatedData,
  ChatbotReplyFinishedData,
  ChatbotReplyStartedData,
  EventData,
  EventListener,
  EventType,
  GenAIGeneratorFinishedData,
  GoalProgressUpdatedData,
  GoalSubscriptionChangedData,
  GoalSubscriptionStrategyEndReachedData,
  MessageUpdatedData,
  NewChatbotTestRequestResponseData,
  NewMediaitemFileIsReadyData,
  NewMessageReceivedData,
  NewMessageReplyEstimationData,
  ScheduleAssignmentsJobStatusUpdatedData,
  TaskProgressUpdatedData
} from '@event-emitter';
import { useAccountsService } from '@hooks/useAccountsService';
import { useProgressService } from '@hooks/useProgressService';
import { AssignmentSchedulingJobStatus } from '@models';
import { useStore } from '@store';

// Configure logging based on environment variable
const isPusherDebugEnabled = process.env.NEXT_PUBLIC_PUSHER_DEBUG === 'true';

// Singleton tracking variables
let isPusherInitialized = false;
let globalPusherInstance: Pusher | null = null;
let subscribedChannelRefCounts: Map<string, number> = new Map();
let channelHandlers: Map<string, Set<(event: EventData) => void>> = new Map();
let channelDispatchers: Map<string, (event: EventData) => void> = new Map();

export interface UseEventsListenerReturn {
  on: <T>(event: EventType, listener: EventListener<T>) => () => void;
}

export function useEventsListener(): UseEventsListenerReturn {
  const [pusherInstance, setPusherInstance] = useState<Pusher | null>(null);
  const { accountMe } = useAccountsService();
  const {
    syncGoalSubscription,
    getChatbotTestRequest,
    setSchedulingInProgress,
    setGoalSubscriptionStrategyFinishedModalState,
    onEvent,
    emitEvent
  } = useStore(
    (state) => ({
      syncGoalSubscription: state.syncGoalSubscription,
      getChatbotTestRequest: state.getChatbotTestRequest,
      setSchedulingInProgress: state.setSchedulingInProgress,
      setGoalSubscriptionStrategyFinishedModalState:
        state.setGoalSubscriptionStrategyFinishedModalState,
      onEvent: state.onEvent,
      emitEvent: state.emitEvent
    }),
    shallow
  );
  const { getTaskProgress, getGoalProgress } = useProgressService();

  const handleEvent = useCallback(
    (event: EventData) => {
      // Emit the event to all listeners
      switch (event.event) {
        case EventType.GOAL_SUBSCRIPTION_CHANGED: {
          const data = event.data as GoalSubscriptionChangedData;
          syncGoalSubscription(
            data.goalEntityId,
            data.goalSubscriptionId,
            data.status,
            data.previousStatus
          );
          emitEvent(EventType.GOAL_SUBSCRIPTION_CHANGED, data);
          break;
        }

        case EventType.NEW_MESSAGE_RECEIVED: {
          const data = event.data as NewMessageReceivedData;
          emitEvent(EventType.NEW_MESSAGE_RECEIVED, data);
          break;
        }

        case EventType.CHATBOT_REPLY_STARTED: {
          const data = event.data as ChatbotReplyStartedData;
          emitEvent(EventType.CHATBOT_REPLY_STARTED, data);
          break;
        }

        case EventType.CHATBOT_REPLY_FINISHED: {
          const data = event.data as ChatbotReplyFinishedData;
          emitEvent(EventType.CHATBOT_REPLY_FINISHED, data);
          break;
        }

        case EventType.MESSAGE_UPDATED: {
          const data = event.data as MessageUpdatedData;
          emitEvent(EventType.MESSAGE_UPDATED, data);
          break;
        }

        case EventType.NEW_MESSAGE_REPLY_ESTIMATION: {
          const data = event.data as NewMessageReplyEstimationData;
          emitEvent(EventType.NEW_MESSAGE_REPLY_ESTIMATION, data);
          break;
        }

        case EventType.NEW_CHATBOT_TEST_REQUEST_RESPONSE: {
          const data = event.data as NewChatbotTestRequestResponseData;
          getChatbotTestRequest(data.chatbotEntityId, data.chatbotTestRequestId);
          emitEvent(EventType.NEW_CHATBOT_TEST_REQUEST_RESPONSE, data);
          break;
        }

        case EventType.NEW_MEDIAITEM_FILE_IS_READY: {
          const data = event.data as NewMediaitemFileIsReadyData;
          emitEvent(EventType.NEW_MEDIAITEM_FILE_IS_READY, data);
          break;
        }

        case EventType.SCHEDULE_ASSIGNMENTS_JOB_STATUS_UPDATED: {
          const data = event.data as ScheduleAssignmentsJobStatusUpdatedData;
          if (
            data.status === AssignmentSchedulingJobStatus.PENDING ||
            data.status === AssignmentSchedulingJobStatus.IN_PROGRESS
          ) {
            setSchedulingInProgress(true, data.timestamp);
          } else {
            setSchedulingInProgress(false, data.timestamp);
          }
          emitEvent(EventType.SCHEDULE_ASSIGNMENTS_JOB_STATUS_UPDATED, data);
          break;
        }

        case EventType.GOAL_SUBSCRIPTION_STRATEGY_END_REACHED: {
          const data = event.data as GoalSubscriptionStrategyEndReachedData;
          setGoalSubscriptionStrategyFinishedModalState({
            isOpen: true,
            goalSubscriptionId: data.goalSubscriptionId
          });
          emitEvent(EventType.GOAL_SUBSCRIPTION_STRATEGY_END_REACHED, data);
          break;
        }

        case EventType.GENAI_GENERATOR_FINISHED: {
          const data = event.data as GenAIGeneratorFinishedData;
          emitEvent(EventType.GENAI_GENERATOR_FINISHED, data);
          break;
        }

        case EventType.BLOCK_OUTPUT_EVALUATED: {
          const data = event.data as BlockOutputEvaluatedData;
          emitEvent(EventType.BLOCK_OUTPUT_EVALUATED, data);
          break;
        }

        case EventType.BLOCK_OUTPUT_AI_USAGE_RECORDED: {
          const data = event.data as BlockOutputAiUsageRecordedData;
          emitEvent(EventType.BLOCK_OUTPUT_AI_USAGE_RECORDED, data);
          break;
        }

        case EventType.TASK_PROGRESS_UPDATED: {
          const data = event.data as TaskProgressUpdatedData;
          getTaskProgress(data.taskEntityId);
          emitEvent(EventType.TASK_PROGRESS_UPDATED, data);
          break;
        }

        case EventType.GOAL_PROGRESS_UPDATED: {
          const data = event.data as GoalProgressUpdatedData;
          getGoalProgress(data.goalEntityId);
          emitEvent(EventType.GOAL_PROGRESS_UPDATED, data);
          break;
        }

        default:
          break;
      }
    },
    [
      syncGoalSubscription,
      getChatbotTestRequest,
      setSchedulingInProgress,
      setGoalSubscriptionStrategyFinishedModalState,
      getTaskProgress,
      getGoalProgress,
      emitEvent
    ]
  );

  // Initialize Pusher
  useEffect(() => {
    // Only initialize Pusher on the client side
    if (typeof window === 'undefined') return;

    // Check for required environment variables
    if (!process.env.NEXT_PUBLIC_PUSHER_KEY || !process.env.NEXT_PUBLIC_PUSHER_CLUSTER) {
      console.error('Missing Pusher configuration. Check your environment variables.');
      return;
    }

    // If Pusher is already initialized, reuse it
    if (isPusherInitialized && globalPusherInstance) {
      setPusherInstance(globalPusherInstance);
      return;
    }

    // Create new Pusher instance if not already initialized
    let pusher: Pusher | null = null;

    try {
      // Configure Pusher logging
      Pusher.logToConsole = isPusherDebugEnabled;

      // Create Pusher instance
      pusher = new Pusher(process.env.NEXT_PUBLIC_PUSHER_KEY, {
        cluster: process.env.NEXT_PUBLIC_PUSHER_CLUSTER
      });

      // Store in global and state
      globalPusherInstance = pusher;
      setPusherInstance(pusher);
      isPusherInitialized = true;

      // Cleanup function
      return () => {
        // Only clean up when there are no more references to this hook
        // We'll let the last instance handle disconnection
        if (!document.hidden) return;

        // Safely disconnect only if the connection exists and is not in a disconnected state
        if (
          pusher &&
          pusher.connection &&
          pusher.connection.state !== 'disconnected' &&
          pusher.connection.state !== 'failed'
        ) {
          if (isPusherDebugEnabled) {
            console.log(`Disconnecting Pusher from state: ${pusher.connection.state}`);
          }
          pusher.disconnect();
          globalPusherInstance = null;
          isPusherInitialized = false;
          subscribedChannelRefCounts.clear();
        }
      };
    } catch (error) {
      console.error('Error initializing Pusher:', error);
    }
  }, []);

  // Set up event listeners
  useEffect(() => {
    // Only proceed if we have both pusher and userId
    if (!pusherInstance || !accountMe?.user?.id) return;

    const CHANNEL_NAME = `user-${btoa(String(accountMe?.user?.id))}`;

    let channel = null;

    // Several components can hold a live useEventsListener at once (e.g. the messages
    // inbox plus an open conversation panel) and they share one Pusher channel. Only one
    // dispatcher is ever bound to that channel, so a Pusher event runs the switch below
    // exactly once no matter how many instances are mounted. Instances register their own
    // handler; every handler drives the same store actions, so the dispatcher invokes the
    // first registered one and falls through to the next when an instance unmounts.
    const handlers = channelHandlers.get(CHANNEL_NAME) ?? new Set<(event: EventData) => void>();
    channelHandlers.set(CHANNEL_NAME, handlers);
    handlers.add(handleEvent);

    const releaseChannelSubscription = () => {
      handlers.delete(handleEvent);
      if (!channel) return;
      const remaining = (subscribedChannelRefCounts.get(CHANNEL_NAME) ?? 1) - 1;
      if (remaining <= 0) {
        subscribedChannelRefCounts.delete(CHANNEL_NAME);
        const dispatcher = channelDispatchers.get(CHANNEL_NAME);
        if (dispatcher) {
          channel.unbind('events', dispatcher);
          channelDispatchers.delete(CHANNEL_NAME);
        }
        channelHandlers.delete(CHANNEL_NAME);
        pusherInstance.unsubscribe(CHANNEL_NAME);
      } else {
        subscribedChannelRefCounts.set(CHANNEL_NAME, remaining);
      }
    };

    try {
      // Subscribe to channel
      channel = pusherInstance.subscribe(CHANNEL_NAME);
      subscribedChannelRefCounts.set(
        CHANNEL_NAME,
        (subscribedChannelRefCounts.get(CHANNEL_NAME) ?? 0) + 1
      );

      if (!channelDispatchers.has(CHANNEL_NAME)) {
        const dispatcher = (event: EventData) => {
          const [firstHandler] = channelHandlers.get(CHANNEL_NAME) ?? [];
          firstHandler?.(event);
        };
        channelDispatchers.set(CHANNEL_NAME, dispatcher);
        channel.bind('events', dispatcher);
      }

      return releaseChannelSubscription;
    } catch (error) {
      console.error('Error in Pusher setup:', error);
      return releaseChannelSubscription;
    }
  }, [accountMe?.user?.id, handleEvent, pusherInstance]);

  // Return the store's onEvent method directly
  return { on: onEvent };
}

export default useEventsListener;
